import React, { useState } from "react";
import {
    Box, Typography, Paper, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, Grid, Tooltip, Snackbar, Alert, useTheme
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DOMPurify from "dompurify";
import api from "../axios/axiosInstance";

const PrivateBlogManager = ({ blogs, refreshBlogs }) => {
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [blogToDelete, setBlogToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const theme = useTheme();

    const handleDelete = async () => {
        try {
            await api.delete(`/grades/blog-private/${blogToDelete.id}/`, {
                data: { id: blogToDelete.id }
            });
            setSnackbar({ open: true, message: "Blog deleted successfully.", severity: "success" });
            setBlogToDelete(null);
            if (refreshBlogs) refreshBlogs();
        } catch (err) {
            console.error("❌ Delete failed:", err);
            setSnackbar({ open: true, message: "Failed to delete blog.", severity: "error" });
        }
    };

    const formatDate = (iso) => new Date(iso).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric"
    });

    return (
        <>
            <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                🗂️ Your Blog Posts
            </Typography>
            <Grid container spacing={2}>
                {blogs.map((blog) => (
                    <Grid item xs={12} key={blog.id}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "#FBFFF5",
                                border: "1px solid #6E8B3D",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}
                        >
                            <Box>
                                <Typography fontWeight="bold">{blog.topic}</Typography>
                                {blog.sub_topic && (
                                    <Typography fontSize="0.875rem" color="text.secondary">
                                        {blog.sub_topic}
                                    </Typography>
                                )}
                                <Typography fontSize="0.75rem" color="text.secondary">
                                    Created: {formatDate(blog.date_created)}
                                </Typography>
                            </Box>

                            <Box display="flex" gap={1}>
                                <Tooltip title="View Blog">
                                    <IconButton onClick={() => setSelectedBlog(blog)}>
                                        <VisibilityIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Blog">
                                    <IconButton onClick={() => setBlogToDelete(blog)}>
                                        <DeleteIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Modals */}
            <Dialog open={!!selectedBlog} onClose={() => setSelectedBlog(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: "#6E8B3D", color: "white", fontWeight: "bold" }}>
                    {selectedBlog?.topic}
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: "#FAFAF5" }}>
                    {selectedBlog?.sub_topic && (
                        <Typography variant="subtitle2" sx={{ fontStyle: "italic", mb: 2, color: "#56643F" }}>
                            {selectedBlog.sub_topic}
                        </Typography>
                    )}
                    <Typography
                        variant="body1"
                        component="div"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(selectedBlog?.blog_details || "")
                        }}
                        sx={{ color: "#3F3F3F", lineHeight: 1.7 }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between" }}>
                    <Typography variant="caption" sx={{ color: "#777" }}>
                        Created: {formatDate(selectedBlog?.date_created)}
                    </Typography>
                    <Button onClick={() => setSelectedBlog(null)} variant="outlined" color="success">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!blogToDelete} onClose={() => setBlogToDelete(null)}>
                <DialogTitle fontWeight="bold" color="error">⚠️ Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the blog titled <strong>{blogToDelete?.topic}</strong>? This action is <strong>irreversible</strong>.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBlogToDelete(null)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default PrivateBlogManager;
